import { useEffect, useState } from "react";
import { EVENT5K } from "@/constants/testIds";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Store, Utensils, Sparkles, Users, AlertCircle } from "lucide-react";

const empty = {
  business_name: "",
  contact_name: "",
  email: "",
  phone: "",
  website: "",
  vendor_type: "small_business",
  description: "",
  needs_power: false,
  needs_table: true,
  is_food_beverage: false,
};

const VENDOR_TYPES = [
  { key: "small_business", label: "Small Business" },
  { key: "food", label: "Food Vendor" },
  { key: "resource_partner", label: "Resource Partner" },
  { key: "community_org", label: "Community Organization" },
];

export function VendorDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [inv, setInv] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/vendor-inventory").then((r) => setInv(r.data)).catch(() => {});
  }, [open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const soldOut = inv?.sold_out;

  const submit = async (e) => {
    e.preventDefault();
    if (soldOut) {
      toast.error("Vendor tables are sold out.");
      return;
    }
    if (!form.business_name || !form.contact_name || !form.email || !form.description) {
      toast.error("Please fill required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/vendors", form);
      setDone(true);
      toast.success("Vendor application received.");
    } catch (err) {
      if (err?.response?.status === 409) toast.error("Vendor tables are sold out.");
      else toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setDone(false); setForm(empty); }}}>
      <DialogContent data-testid={EVENT5K.vendorDialog} className="max-w-2xl bg-[#0A0A0A] border-[#27272A] text-white max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#EC008C] font-bold">Vendor Application</div>
          <DialogTitle className="font-display text-3xl uppercase">Bring your booth to race day.</DialogTitle>
          <DialogDescription className="text-[#A3A3A3]">
            Small businesses, food vendors, resource partners, and community organizations welcome.
          </DialogDescription>
        </DialogHeader>

        {/* Live inventory banner */}
        {inv && (
          soldOut ? (
            <div data-testid="vendor-sold-out-banner" className="bg-black border-2 border-[#EC008C] p-4 text-center">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#EC008C] font-bold">Sold Out</div>
              <div className="font-display text-2xl uppercase text-white mt-1">Vendor Tables Sold Out</div>
              <div className="text-xs text-[#A3A3A3] mt-2">All {inv.max_tables} vendor spaces have been reserved for this event. Thank you for your interest!</div>
            </div>
          ) : (
            <div data-testid="vendor-inventory-banner" className="bg-[#00A651]/10 border border-[#00A651]/40 p-3 text-center">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#00A651] font-bold">Live Availability</div>
              <div className="font-display text-lg uppercase text-white mt-0.5">
                <span data-testid="vendor-tables-remaining">{inv.remaining}</span> of {inv.max_tables} tables remaining · ${inv.previous_vendor_price} promo through Sept 30
              </div>
            </div>
          )
        )}

        {done ? (
          <div className="py-4">
            <div className="bg-[#141414] border border-[#EC008C]/50 p-6">
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#EC008C] font-bold">Application received</div>
              <p className="mt-2 text-[#E5E5E5]">
                Thank you for applying! Your $50 payment reserves your table via RunSignup — please complete payment there to finalize your spot.
              </p>
              {form.is_food_beverage && (
                <div className="mt-4 p-3 bg-[#EC008C] text-white text-sm">
                  <strong>Food &amp; Beverage vendors:</strong> please email your Certificate of Insurance (COI) to <a href="mailto:admin@businessshowers.org" className="underline">admin@businessshowers.org</a>. Your status will remain <em>Reserved — COI Pending</em> until we approve it.
                </div>
              )}
            </div>
            <button className="mt-6 border-2 border-white/40 hover:border-[#00A651] hover:text-[#00A651] font-bold uppercase tracking-widest text-sm px-6 py-3" onClick={() => onOpenChange(false)}>Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Business name" required>
                <Input data-testid={EVENT5K.vendorBusinessName} value={form.business_name} onChange={(e) => update("business_name", e.target.value)} required disabled={soldOut} />
              </Field>
              <Field label="Contact name" required>
                <Input data-testid={EVENT5K.vendorContactName} value={form.contact_name} onChange={(e) => update("contact_name", e.target.value)} required disabled={soldOut} />
              </Field>
              <Field label="Email" required>
                <Input data-testid={EVENT5K.vendorEmail} type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required disabled={soldOut} />
              </Field>
              <Field label="Phone">
                <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} disabled={soldOut} />
              </Field>
              <Field label="Website / social">
                <Input value={form.website} onChange={(e) => update("website", e.target.value)} placeholder="https://" disabled={soldOut} />
              </Field>
              <Field label="Vendor type">
                <Select value={form.vendor_type} onValueChange={(v) => update("vendor_type", v)} disabled={soldOut}>
                  <SelectTrigger data-testid={EVENT5K.vendorType} className="bg-black border-[#27272A]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-[#27272A] text-white">
                    {VENDOR_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="What will you offer?" required>
              <Textarea data-testid={EVENT5K.vendorDescription} rows={3} value={form.description} onChange={(e) => update("description", e.target.value)} required disabled={soldOut} />
            </Field>

            {/* Food & Beverage question */}
            <div className="bg-[#141414] border border-[#27272A] p-4">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#EC008C] font-bold mb-2">Food &amp; Beverage</div>
              <Label className="text-sm text-white">Will you be selling, serving, sampling, or distributing food or beverages?</Label>
              <div className="mt-3 flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="is_food_beverage" data-testid="vendor-fb-yes" checked={form.is_food_beverage === true} onChange={() => update("is_food_beverage", true)} disabled={soldOut} className="accent-[#EC008C]" />
                  <span className="text-sm text-white">Yes</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="is_food_beverage" data-testid="vendor-fb-no" checked={form.is_food_beverage === false} onChange={() => update("is_food_beverage", false)} disabled={soldOut} className="accent-[#00A651]" />
                  <span className="text-sm text-white">No</span>
                </label>
              </div>
              {form.is_food_beverage && (
                <div data-testid="vendor-coi-notice" className="mt-4 p-3 bg-[#EC008C]/15 border border-[#EC008C] text-white text-xs leading-relaxed">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-[#EC008C] flex-shrink-0 mt-0.5" />
                    <div>
                      <strong className="uppercase tracking-widest text-[10px] block mb-1">Food &amp; Beverage Vendors</strong>
                      A valid Certificate of Insurance (COI) is required to participate. You may reserve your vendor space with payment; however, final vendor approval is contingent upon receipt and approval of the required COI. Email your COI to <a href="mailto:admin@businessshowers.org" className="underline">admin@businessshowers.org</a> after payment.
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-6 pt-1">
              <label className="flex items-center gap-3 cursor-pointer">
                <Switch checked={form.needs_table} onCheckedChange={(v) => update("needs_table", v)} disabled={soldOut} />
                <span className="text-sm">I need a table</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <Switch checked={form.needs_power} onCheckedChange={(v) => update("needs_power", v)} disabled={soldOut} />
                <span className="text-sm">I need power</span>
              </label>
            </div>
            <button data-testid={EVENT5K.vendorSubmit} disabled={submitting || soldOut} className="w-full bg-[#EC008C] hover:bg-[#FF1FA8] disabled:bg-[#27272A] disabled:cursor-not-allowed text-white font-bold uppercase tracking-widest text-sm py-4 transition-all">
              {soldOut ? "Sold Out" : submitting ? "Submitting…" : "Reserve Your Table →"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function VendorSection({ onApply }) {
  const perks = [
    { icon: Store, title: "Small Business Booths", desc: "Sell products, share services, and connect with buyers." },
    { icon: Utensils, title: "Food & Beverage", desc: "Fuel runners and families with your local flavor." },
    { icon: Sparkles, title: "Resource Partners", desc: "Educate attendees on programs, grants, and support." },
    { icon: Users, title: "Community Orgs", desc: "Show up for the mission and grow your following." },
  ];

  const [inv, setInv] = useState(null);
  useEffect(() => {
    api.get("/vendor-inventory").then((r) => setInv(r.data)).catch(() => {});
  }, []);

  return (
    <section id="vendor" data-testid={EVENT5K.sectionVendor} className="relative bg-[#0A0A0A] py-24 md:py-32 border-t border-[#27272A]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#EC008C] font-bold mb-4">Vendor Village</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white">
              Set up shop in the <span className="text-[#00A651]">vendor village</span>.
            </h2>
            <p className="mt-4 text-lg text-[#A3A3A3]">
              A family-friendly, high-foot-traffic community event with runners, walkers, families, and supporters
              ready to meet your business. Bring your booth, your table, and your energy.
            </p>

            {/* Live inventory pill */}
            {inv && (
              <div data-testid="vendor-section-inventory" className="mt-6 inline-flex items-center gap-2 px-4 py-2 border border-[#00A651]/40 bg-[#00A651]/10">
                <span className={`w-2 h-2 rounded-full ${inv.sold_out ? "bg-[#EC008C]" : "bg-[#00A651] animate-pulse"}`} />
                <span className="text-[10px] uppercase tracking-[0.24em] font-bold text-white">
                  {inv.sold_out ? "Vendor Tables Sold Out" : `${inv.remaining} of ${inv.max_tables} tables · $${inv.previous_vendor_price} thru Sept 30`}
                </span>
              </div>
            )}

            <div>
              <button
                data-testid={EVENT5K.vendorApplyBtn}
                onClick={onApply}
                disabled={inv?.sold_out}
                className="mt-6 bg-[#EC008C] hover:bg-[#FF1FA8] disabled:bg-[#27272A] disabled:cursor-not-allowed text-white font-bold uppercase tracking-widest text-sm px-8 py-4 transition-all hover:-translate-y-0.5"
              >
                {inv?.sold_out ? "Vendor Tables Sold Out" : "Reserve Your Vendor Table →"}
              </button>
            </div>

            <div className="mt-8 relative overflow-hidden border border-[#27272A]">
              <img
                src="/images/ebony-tonja.jpg"
                alt="Ebony Finch (2024 Top Winner) and Tonja Lindsay (2025 Finalist &amp; 5K Vendor)"
                loading="lazy"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#EC008C] font-bold">Alum Spotlight</div>
                <div className="mt-1 font-display text-base md:text-lg font-bold uppercase text-white leading-tight">
                  Ebony Finch · 2024 Top Winner<br />
                  Tonja Lindsay · 2025 Finalist &amp; 5K Vendor
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {perks.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-[#141414] border border-[#27272A] p-6 hover:border-[#EC008C]/50 transition-colors">
                <Icon className="w-6 h-6 text-[#00A651] mb-3" />
                <div className="font-display font-bold uppercase text-white text-lg">{title}</div>
                <div className="text-sm text-[#A3A3A3] mt-1">{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.24em] text-[#A3A3A3]">
        {label} {required && <span className="text-[#EC008C]">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
